/**
 * frontend/hooks/useInviter.js — checks an invite code with the backend.
 *
 * useInviter(code) → { status, displayName }
 *   'none'      no code
 *   'checking'  request in flight
 *   'valid'     the code belongs to an onboarded user (displayName may be null)
 *   'invalid'   unknown / malformed — the stored code has been removed
 *   'unknown'   network or server error — the code is kept, nothing is shown
 */

import { useEffect, useState } from 'react';
import { clearReferralCode, normalizeReferralCode, verifyReferrer } from '../lib/referral';

export function inviteBannerText(displayName) {
  return displayName
    ? `🎉 ${displayName} invited you to Big Creative`
    : "🎉 You've been invited to Big Creative";
}

export function useInviter(code) {
  const c = normalizeReferralCode(code);
  const [state, setState] = useState({ status: c ? 'checking' : 'none', displayName: null });

  useEffect(() => {
    if (!c) { setState({ status: 'none', displayName: null }); return undefined; }
    let cancelled = false;
    setState({ status: 'checking', displayName: null });
    (async () => {
      const r = await verifyReferrer(c);
      if (cancelled) return;
      if (r?.valid) setState({ status: 'valid', displayName: r.displayName });
      else if (r && r.valid === false) { clearReferralCode(); setState({ status: 'invalid', displayName: null }); }
      else setState({ status: 'unknown', displayName: null });
    })();
    return () => { cancelled = true; };
  }, [c]);

  return state;
}
